import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'
import type { NotificationTimeframe } from './types'

type UserSettings = {
  language?: string
  theme?: string
  summary?: NotificationTimeframe
  alert?: boolean
  hideDeleted?: boolean
}

function getUserSettings(): UserSettings {
  const saved = localStorage.getItem('userSettings')
  return saved ? JSON.parse(saved) : {}
}

function saveUserSettings(newSettings: UserSettings) {
  const existing = getUserSettings()
  const updated = { ...existing, ...newSettings }
  localStorage.setItem('userSettings', JSON.stringify(updated))
}

export const useUserSettingsStore = defineStore('userSettings', () => {
  // Load existing settings from localStorage
  const saved = getUserSettings()

  // THEME (dark/light)
  const theme = ref(saved.theme ?? 'light')

  // LANGUAGE
  let browserLang = navigator.language.split('-')[0]
  if (browserLang !== 'en' && browserLang !== 'de') {
    browserLang = 'en'
  }
  const language = ref(saved.language ?? browserLang ?? 'en')

  // SUMMARY (default 1 week)
  const summary = ref<NotificationTimeframe>(saved.summary ?? '1week')

  // ALERT (default false)
  const alert = ref<boolean>(saved.alert ?? false)

  // HIDE DELETED (default true)
  const hideDeleted = ref<boolean>(saved.hideDeleted ?? true)

  function updateSettings(settings: UserSettings) {
    if (settings.theme) {
      theme.value = settings.theme
      document.documentElement.classList.toggle('dark', theme.value === 'dark')
    }

    if (settings.language) {
      language.value = settings.language
    }

    if (settings.summary) {
      summary.value = settings.summary
    }

    if (typeof settings.alert === 'boolean') {
      alert.value = settings.alert
    }

    if (typeof settings.hideDeleted === 'boolean') {
      hideDeleted.value = settings.hideDeleted
    }

    saveUserSettings(settings)
  }

  return {
    theme: readonly(theme),
    language: readonly(language),
    summary: readonly(summary),
    alert: readonly(alert),
    hideDeleted: readonly(hideDeleted),
    updateSettings,
  }
})
